import { CMS_BASE_URL } from './constants'
import {
  extractStrapiMediaUrl,
  readString,
  toStrapiEntity,
  toStrapiEntityCollection,
} from './utils/strapiPayload'

import type {
  SoundscapeFeatureBlockCmsContent,
  SoundscapeTextCardCmsContent,
  SoundscapePageCmsContent,
  SoundscapePerformanceSpecCmsContent,
  SoundscapeSolutionCmsContent,
  SoundscapeOnePlatformStepCmsContent,
} from './interfaces'

interface FetchSoundscapeContentOptions {
  signal?: AbortSignal
}

const SOUNDSCAPE_PAGE_ENDPOINT = `/api/soundscape-page?${
  [
    'populate[ctaBackgroundImage][fields][0]=url',
    'populate[featureBlocks][populate][imageDesktop][fields][0]=url',
    'populate[featureBlocks][populate][imageMobile][fields][0]=url',
    'populate[featureBlocks][populate][contentItems]=true',
    'populate[showcaseCards]=true',
    'populate[cyberBuckFeatureCards]=true',
    'populate[solutionsCards][populate][logo][fields][0]=url',
    'populate[solutionsCards][populate][backgroundImage][fields][0]=url',
    'populate[onePlatformSteps][populate][image][fields][0]=url',
    'populate[onePlatformHardwareSupportItems]=true',
    'populate[performanceSpecsCards]=true',
  ].join('&')
}`

const readMediaUrl = (value: unknown): string | null => extractStrapiMediaUrl(value, CMS_BASE_URL)

const readOptionalString = (value: unknown): string | undefined => {
  const text = readString(value)

  return text ?? undefined
}

const mapFeatureBlocks = (value: unknown): SoundscapeFeatureBlockCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((block): SoundscapeFeatureBlockCmsContent[] => {
      const imageDesktopUrl = readMediaUrl(block.imageDesktop)
      const imageMobileUrl = readMediaUrl(block.imageMobile)
      const title = readString(block.title)
      const pillText = readString(block.pillText)
      const contentHeading = readString(block.contentHeading)
      const contentItems = toStrapiEntityCollection(block.contentItems)
        .map((item) => readString(item.text))
        .filter((item): item is string => item !== null)

      if (
        imageDesktopUrl === null ||
        imageMobileUrl === null ||
        title === null ||
        pillText === null ||
        contentHeading === null ||
        contentItems.length === 0
      ) {
        return []
      }

      return [{ imageDesktopUrl, imageMobileUrl, title, pillText, contentHeading, contentItems }]
    })
)

const mapTextCards = (value: unknown): SoundscapeTextCardCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((card): SoundscapeTextCardCmsContent[] => {
      const text = readString(card.text)

      if (text === null) {
        return []
      }

      return [{ text }]
    })
)

const mapSolutions = (value: unknown): SoundscapeSolutionCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((solution): SoundscapeSolutionCmsContent[] => {
      const logoUrl = readMediaUrl(solution.logo)
      const backgroundImageUrl = readMediaUrl(solution.backgroundImage)
      const description = readString(solution.description)
      const popupTitle = readString(solution.popupTitle)
      const popupDescription = readString(solution.popupDescription)

      if (
        logoUrl === null ||
        backgroundImageUrl === null ||
        description === null ||
        popupTitle === null ||
        popupDescription === null
      ) {
        return []
      }

      return [{ logoUrl, backgroundImageUrl, description, popupTitle, popupDescription }]
    })
)

const mapSteps = (value: unknown): SoundscapeOnePlatformStepCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((step): SoundscapeOnePlatformStepCmsContent[] => {
      const title = readString(step.title)
      const imageUrl = readMediaUrl(step.image)

      if (title === null || imageUrl === null) {
        return []
      }

      return [{ title, imageUrl }]
    })
)

const mapPerformanceSpecs = (value: unknown): SoundscapePerformanceSpecCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((spec): SoundscapePerformanceSpecCmsContent[] => {
      const body = readString(spec.body)

      if (body === null) {
        return []
      }

      return [{ body }]
    })
)

const mapSoundscapePageContent = (payload: unknown): SoundscapePageCmsContent | null => {
  const payloadRoot = toStrapiEntity(payload)
  const contentRoot = toStrapiEntity(payloadRoot?.data || payloadRoot)

  if (!contentRoot) {
    return null
  }

  const heroPillText = readString(contentRoot.heroPillText)
  const heroTitleText = readString(contentRoot.heroTitleText)
  const heroDescription = readString(contentRoot.heroDescription)

  const ctaText = readString(contentRoot.ctaText)
  const ctaBackgroundImageUrl = readMediaUrl(contentRoot.ctaBackgroundImage)

  const featuresTitle = readString(contentRoot.featuresTitle)
  const featureBlocks = mapFeatureBlocks(contentRoot.featureBlocks)

  const showcasePillText = readString(contentRoot.showcasePillText)
  const showcaseTitle = readString(contentRoot.showcaseTitle)
  const showcaseDescription = readString(contentRoot.showcaseDescription)
  const showcaseCards = mapTextCards(contentRoot.showcaseCards)

  const cyberBuckPillText = readString(contentRoot.cyberBuckPillText)
  const cyberBuckTitle = readString(contentRoot.cyberBuckTitle)
  const cyberBuckFeatureCards = mapTextCards(contentRoot.cyberBuckFeatureCards)
  const cyberBuckBookDemoTitle = readString(contentRoot.cyberBuckBookDemoTitle)
  const cyberBuckBookDemoButtonText = readString(contentRoot.cyberBuckBookDemoButtonText)
  const cyberBuckBookDemoButtonUrl = readString(contentRoot.cyberBuckBookDemoButtonUrl)

  const solutionsTitle = readString(contentRoot.solutionsTitle)
  const solutionsCards = mapSolutions(contentRoot.solutionsCards)

  const onePlatformTitle = readString(contentRoot.onePlatformTitle)
  const onePlatformSteps = mapSteps(contentRoot.onePlatformSteps)
  const onePlatformHardwareSupportTitle = readString(contentRoot.onePlatformHardwareSupportTitle)
  const onePlatformHardwareSupportItems = toStrapiEntityCollection(contentRoot.onePlatformHardwareSupportItems)
    .map((item) => readString(item.text))
    .filter((item): item is string => item !== null)

  const performanceSpecsTitle = readString(contentRoot.performanceSpecsTitle)
  const performanceSpecsCards = mapPerformanceSpecs(contentRoot.performanceSpecsCards)

  const bottomHeroTitleLine1 = readString(contentRoot.bottomHeroTitleLine1)
  const bottomHeroTitleLine2 = readOptionalString(contentRoot.bottomHeroTitleLine2)
  const bottomHeroButtonText = readString(contentRoot.bottomHeroButtonText)
  const bottomHeroButtonUrl = readString(contentRoot.bottomHeroButtonUrl)

  if (
    heroPillText === null ||
    heroTitleText === null ||
    heroDescription === null ||
    ctaText === null ||
    ctaBackgroundImageUrl === null ||
    featuresTitle === null ||
    featureBlocks.length !== 2 ||
    showcasePillText === null ||
    showcaseTitle === null ||
    showcaseDescription === null ||
    showcaseCards.length !== 3 ||
    cyberBuckPillText === null ||
    cyberBuckTitle === null ||
    cyberBuckFeatureCards.length !== 4 ||
    cyberBuckBookDemoTitle === null ||
    cyberBuckBookDemoButtonText === null ||
    cyberBuckBookDemoButtonUrl === null ||
    solutionsTitle === null ||
    solutionsCards.length !== 5 ||
    onePlatformTitle === null ||
    onePlatformSteps.length !== 3 ||
    onePlatformHardwareSupportTitle === null ||
    onePlatformHardwareSupportItems.length !== 3 ||
    performanceSpecsTitle === null ||
    performanceSpecsCards.length !== 6 ||
    bottomHeroTitleLine1 === null ||
    bottomHeroButtonText === null ||
    bottomHeroButtonUrl === null
  ) {
    return null
  }

  return {
    hero: {
      pillText: heroPillText,
      titleText: heroTitleText,
      description: heroDescription,
    },
    cta: {
      text: ctaText,
      backgroundImageUrl: ctaBackgroundImageUrl,
    },
    features: {
      title: featuresTitle,
      blocks: featureBlocks,
    },
    showcase: {
      pillText: showcasePillText,
      title: showcaseTitle,
      description: showcaseDescription,
      cards: showcaseCards,
    },
    cyberBuck: {
      pillText: cyberBuckPillText,
      title: cyberBuckTitle,
      featureCards: cyberBuckFeatureCards,
      bookDemoTitle: cyberBuckBookDemoTitle,
      bookDemoButtonText: cyberBuckBookDemoButtonText,
      bookDemoButtonUrl: cyberBuckBookDemoButtonUrl,
    },
    solutions: {
      title: solutionsTitle,
      items: solutionsCards,
    },
    onePlatform: {
      title: onePlatformTitle,
      steps: onePlatformSteps,
      hardwareSupportTitle: onePlatformHardwareSupportTitle,
      hardwareSupportItems: onePlatformHardwareSupportItems,
    },
    performanceSpecs: {
      title: performanceSpecsTitle,
      cards: performanceSpecsCards,
    },
    bottomHero: {
      titleLine1: bottomHeroTitleLine1,
      titleLine2: bottomHeroTitleLine2,
      buttonText: bottomHeroButtonText,
      buttonUrl: bottomHeroButtonUrl,
    },
  }
}

export const fetchSoundscapeContentFromStrapi = async (
  options: FetchSoundscapeContentOptions = {},
): Promise<SoundscapePageCmsContent | null> => {
  const response = await fetch(`${CMS_BASE_URL}${SOUNDSCAPE_PAGE_ENDPOINT}`, {
    signal: options.signal,
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch Soundscape CMS content: ${response.status}`)
  }

  const content = mapSoundscapePageContent(await response.json())

  if (!content) {
    throw new Error('Soundscape CMS content missing required fields')
  }

  return content
}
